import { useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormActions } from "@jbnl/shared-ui";
import { TEXT_FIELD } from "@/features/standaardteksten/useTextPresets";
import { PresetChips } from "./PresetChips";
import { useSetBoekjegegevens, type Wedstrijddag } from "./useWedstrijden";
import { WedstrijdleiderPicker } from "./WedstrijdleiderPicker";

/** The boekje's own submit shape: an untouched optional field is null, never an empty string. */
function naarBoekjeWaarden(velden: {
  id: string;
  briefing: string;
  opmerkingen: string;
  telefoonnummer: string;
  wedstrijdleider: string;
  meldtijd: string;
}) {
  return {
    id: velden.id,
    briefing: velden.briefing.trim() || null,
    // One line per regel: the template repeats a row per opmerking, so the split is
    // where the model already is rather than a formatting choice made at print time.
    opmerkingen: velden.opmerkingen.split("\n"),
    telefoonnummer: velden.telefoonnummer.trim() || null,
    wedstrijdleider: velden.wedstrijdleider.trim() || null,
    meldtijd: velden.meldtijd.trim() || null,
  };
}

/**
 * A card of its own, because a dag is entered from a calendar and these are filled in months
 * later off a proef. Both fields stay free text - the picker below fills them once, and a
 * beheerder's own edit afterward is never overwritten by anything but a second pick (ADR-0072).
 */
export function Boekjegegevens({
  seizoenId,
  dag,
}: {
  seizoenId: string;
  dag: Wedstrijddag;
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const opslaan = useSetBoekjegegevens(seizoenId);

  const [briefing, setBriefing] = useState(dag.briefing ?? "");
  const [opmerkingen, setOpmerkingen] = useState(dag.opmerkingen.join("\n"));
  const [telefoonnummer, setTelefoonnummer] = useState(
    dag.telefoonnummer ?? "",
  );
  const [wedstrijdleider, setWedstrijdleider] = useState(
    dag.wedstrijdleider ?? "",
  );
  const [meldtijd, setMeldtijd] = useState(dag.meldtijd ?? "");

  // A preset is one more line, not a replacement: opmerkingen is already a running list (#177).
  const appendLine = (text: string) =>
    setOpmerkingen((current) =>
      current.trim().length === 0 ? text : `${current}\n${text}`,
    );

  // The picker's fill: both fields, together, exactly as a hand-typed edit would set them.
  const kiesWedstrijdleider = (
    gekozenNaam: string,
    gekozenTelefoonnummer: string | null,
  ) => {
    setWedstrijdleider(gekozenNaam);
    setTelefoonnummer(gekozenTelefoonnummer ?? "");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("boekje.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            opslaan.mutate(
              naarBoekjeWaarden({
                id: dag.id,
                briefing,
                opmerkingen,
                telefoonnummer,
                wedstrijdleider,
                meldtijd,
              }),
            );
          }}
        >
          <BoekjeVelden
            t={t}
            wedstrijddagId={dag.id}
            briefing={briefing}
            onBriefingChange={setBriefing}
            opmerkingen={opmerkingen}
            onOpmerkingenChange={setOpmerkingen}
            onOpmerkingenPresetSelect={appendLine}
            telefoonnummer={telefoonnummer}
            onTelefoonnummerChange={setTelefoonnummer}
            wedstrijdleider={wedstrijdleider}
            onWedstrijdleiderChange={setWedstrijdleider}
            onWedstrijdleiderKies={kiesWedstrijdleider}
            meldtijd={meldtijd}
            onMeldtijdChange={setMeldtijd}
          />

          <FormActions>
            {opslaan.error && (
              <p className="text-sm text-destructive">
                {opslaan.error.message}
              </p>
            )}
            {/* Not a bare "Opslaan": the wedstrijddag's own form sits on the same screen with
                one of those, and two buttons that read the same save different things. */}
            <Button type="submit" disabled={opslaan.isPending}>
              {opslaan.isPending
                ? t("common:actions.busy")
                : t("boekje.opslaan")}
            </Button>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  );
}

function BoekjeVelden({
  t,
  wedstrijddagId,
  briefing,
  onBriefingChange,
  opmerkingen,
  onOpmerkingenChange,
  onOpmerkingenPresetSelect,
  telefoonnummer,
  onTelefoonnummerChange,
  wedstrijdleider,
  onWedstrijdleiderChange,
  onWedstrijdleiderKies,
  meldtijd,
  onMeldtijdChange,
}: {
  t: TFunction<["wedstrijden", "common"]>;
  wedstrijddagId: string;
  briefing: string;
  onBriefingChange: (waarde: string) => void;
  opmerkingen: string;
  onOpmerkingenChange: (waarde: string) => void;
  /** A preset appends a line rather than replacing the field, unlike typing (#177). */
  onOpmerkingenPresetSelect: (tekst: string) => void;
  telefoonnummer: string;
  onTelefoonnummerChange: (waarde: string) => void;
  wedstrijdleider: string;
  onWedstrijdleiderChange: (waarde: string) => void;
  onWedstrijdleiderKies: (naam: string, telefoonnummer: string | null) => void;
  meldtijd: string;
  onMeldtijdChange: (waarde: string) => void;
}) {
  return (
    <>
      <Field>
        <FieldLabel htmlFor="dag-wedstrijdleider">
          {t("boekje.wedstrijdleider")}
        </FieldLabel>
        <Input
          id="dag-wedstrijdleider"
          value={wedstrijdleider}
          maxLength={200}
          onChange={(e) => onWedstrijdleiderChange(e.target.value)}
        />
        <WedstrijdleiderPicker
          bron={{ soort: "wedstrijddag", wedstrijddagId }}
          onKies={onWedstrijdleiderKies}
        />
        <FieldDescription>{t("boekje.wedstrijdleiderUitleg")}</FieldDescription>
      </Field>

      <Field>
        <FieldLabel htmlFor="dag-meldtijd">{t("boekje.meldtijd")}</FieldLabel>
        <Input
          id="dag-meldtijd"
          value={meldtijd}
          maxLength={200}
          onChange={(e) => onMeldtijdChange(e.target.value)}
        />
        <PresetChips
          field={TEXT_FIELD.reportingTime}
          onSelect={onMeldtijdChange}
        />
        <FieldDescription>{t("boekje.meldtijdUitleg")}</FieldDescription>
      </Field>

      <Field>
        <FieldLabel htmlFor="dag-briefing">{t("boekje.briefing")}</FieldLabel>
        <Input
          id="dag-briefing"
          value={briefing}
          maxLength={200}
          onChange={(e) => onBriefingChange(e.target.value)}
        />
        <PresetChips field={TEXT_FIELD.briefing} onSelect={onBriefingChange} />
        <FieldDescription>{t("boekje.briefingUitleg")}</FieldDescription>
      </Field>

      <Field>
        <FieldLabel htmlFor="dag-opmerkingen">
          {t("boekje.opmerkingen")}
        </FieldLabel>
        <Textarea
          id="dag-opmerkingen"
          rows={3}
          value={opmerkingen}
          onChange={(e) => onOpmerkingenChange(e.target.value)}
        />
        <PresetChips
          field={TEXT_FIELD.remarks}
          onSelect={onOpmerkingenPresetSelect}
        />
        <FieldDescription>{t("boekje.opmerkingenUitleg")}</FieldDescription>
      </Field>

      <Field>
        <FieldLabel htmlFor="dag-telefoon">{t("boekje.telefoon")}</FieldLabel>
        <Input
          id="dag-telefoon"
          value={telefoonnummer}
          maxLength={200}
          onChange={(e) => onTelefoonnummerChange(e.target.value)}
        />
        <FieldDescription>{t("boekje.telefoonUitleg")}</FieldDescription>
      </Field>
    </>
  );
}
